<template>
    <div>
        <div class="flex flex-row justify-between items-end gap-2 w-full px-3 border-b pb-1 border-b-gray-300">
            <div class="flex flex-col text-gray-500">
                <slot name="overline" />

                <div class="flex flex-row items-end gap-2">
                    <h3 class="font-semibold text-gray-700 text-3xl">
                        {{ props.title }}
                    </h3>

                    <LinkButton
                        class="text-gray-700 bg-gray-50"
                        size="Small"
                        variant="Transparent-Light"
                        :icon-description="$t('open-allocation-group-in-new-tab', { allocationGroupName })"
                        :open-in-new-tab="true"
                        :to="props.linkTarget"
                    >
                        <template #icon>
                            <ExternalLinkIcon class="size-4.5" />
                        </template>
                    </LinkButton>
                </div>
            </div>

            <div class="flex flex-row items-center gap-1">
                <Badge size="Small" :variant="props.statusColor">
                    {{ props.statusLabel }}
                </Badge>

                <VisibilityBadge size="Small" :visible-items="props.countVisible" :total-items="props.countTotal">
                    <template #tooltip-text>
                        <template v-if="props.countVisible > 0">
                            <i18n path="count-visible-allocations-variants-out-of-total">
                                <template #countVisible>
                                    <span class="font-semibold">{{ props.countVisible }}</span>
                                </template>

                                <template #countTotal>
                                    <span class="font-semibold">{{ props.countTotal }}</span>
                                </template>
                            </i18n>
                        </template>
                        <template v-else>
                            <i18n path="no-allocation-variants-visible">
                                <template #countTotal>
                                    <span class="font-semibold">{{ props.countTotal }}</span>
                                </template>
                            </i18n>
                        </template>
                    </template>
                </VisibilityBadge>

                <Tooltip>
                    <template #trigger="tooltipProps">
                        <button
                            class="hover:bg-gray-700 hover:text-gray-50 active:bg-gray-900 active:text-gray-50 rounded ml-1 cursor-pointer p-1 focus-visible:outline-inset focus-visible:outline-2 focus-visible:outline-gray-700"
                            :aria-expanded="props.isExpanded"
                            :aria-controls="contentId"
                            v-bind="tooltipProps"
                            @click="
                                triggerHideTooltip();
                                emit('toggle-is-expanded');
                            "
                        >
                            <ChevronRightIcon
                                class="size-6 rounded-xs transition-transform"
                                :class="[props.isExpanded ? '-rotate-90' : 'rotate-90']"
                            />
                        </button>
                    </template>

                    <template #text>
                        <template v-if="props.isExpanded">
                            {{
                                $t('collapse-allocation-group', {
                                    allocationGroupName,
                                })
                            }}
                        </template>
                        <template v-else>
                            {{
                                $t('expand-allocation-group', {
                                    allocationGroupName,
                                })
                            }}
                        </template>
                    </template>
                </Tooltip>
            </div>
        </div>

        <div
            :id="contentId"
            class="mt-1 transition-[height] overflow-hidden"
            :inert="!props.isExpanded"
            :class="[props.isExpanded ? 'h-auto' : 'h-0']"
        >
            <slot />
        </div>
    </div>
</template>

<script setup lang="ts">
import { useFluent } from 'fluent-vue';
import { nanoid } from 'nanoid';
import { computed, inject, provide, ref } from 'vue';
import { RouteLocationRaw } from 'vue-router';
import Badge, { BadgeVariant } from '../Badge/Badge.vue';
import LinkButton from '../Button/LinkButton.vue';
import ChevronRightIcon from '../Icon/ChevronRightIcon.vue';
import ExternalLinkIcon from '../Icon/ExternalLinkIcon.vue';
import { triggerHideTooltipKey } from '../Tooltip/injectionKeys';
import Tooltip from '../Tooltip/Tooltip.vue';
import VisibilityBadge from '../VisibilityBadge/VisibilityBadge.vue';
import { allocationGroupKey } from './injectionKeys';

type AllocationGroupSectionProps = {
    title: string;
    linkTarget: RouteLocationRaw;
    isExpanded: boolean;
    statusColor: BadgeVariant;
    statusLabel: string;
    countVisible: number;
    countTotal: number;
};

type AllocationGroupSectionEmits = {
    (e: 'toggle-is-expanded'): void;
};

const props = defineProps<AllocationGroupSectionProps>();
const emit = defineEmits<AllocationGroupSectionEmits>();
const { $t } = useFluent();

const contentId = nanoid();

const injectedValues = inject(allocationGroupKey);
if (!injectedValues) {
    throw new Error('AllocationGroupSection must be used within a AllocationSelectionPanel component');
}

const allocationGroupName = computed(() => $t('allocation-group-name', { allocationGroupType: injectedValues.type }));

// This is used to force close the Tooltip after clicking the button
const triggerHideTooltipKeyCounter = ref(0);
provide(triggerHideTooltipKey, triggerHideTooltipKeyCounter);

function triggerHideTooltip() {
    triggerHideTooltipKeyCounter.value = triggerHideTooltipKeyCounter.value + 1;
}
</script>
